import { Allotment } from 'allotment'
import { useEffect, useState } from 'react'

import { useDropdown } from '@/entities/dropdowns/libs/useDropdown'
import { groupsSelectors, saveGroup, toggleGroupStatus } from '@/entities/groups'
import { Icon, IconButton, ToggleButton } from '@/shared/components'
import { MapFieldValue } from '@/shared/components/AddressFields/MapField'
import { DynamicForm } from '@/shared/components/DynamicForm/DynamicForm'
import { CustomGridToolbar } from '@/shared/components/GridTable/CustomGridToolbar'
import { GridTable } from '@/shared/components/GridTable/GridTable'
import { useToastContext } from '@/shared/components/Toast/ToastContext'
import { DEFAULT_MAP_CENTER_LAT, DEFAULT_MAP_CENTER_LON, DEFAULT_MAP_ZOOM } from '@/shared/config/consts'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { faAdd, faClose, faEdit, faToggleOn } from '@fortawesome/pro-regular-svg-icons'
import { Divider, Grid, styled } from '@mui/material'
import { GridColDef, GridInputRowSelectionModel, GridValidRowModel } from '@mui/x-data-grid-premium'

import { getGroupFormDefinition, initialGroupFormValues } from './libs/getGroupFormDefinition'
import {
  StyledAddButton, StyledButtonsContainer, StyledContainer, StyledDescription, StyledHeader, StyledLeftPane, StyledRightPane,
} from './StyledOutletPage'

export type GroupsPageProps = {
  //
}

export const GroupsPage: React.FC<GroupsPageProps> = () => {
  const [rowSelectionModel, setRowSelectionModel] = useState<GridInputRowSelectionModel>()
  const [showDetails, setShowDetails] = useState(false)
  const [formType, setFormType] = useState<'add' | 'edit' | 'view'>('add')
  const [isSubmitting, setSubmitting] = useState(false)
  const [formValues, setFormValues] = useState<Record<string, unknown>>({})
  const groups = useAppSelector(state => groupsSelectors.selectAll(state))

  const dispatch = useAppDispatch()
  const { data: timeZonesOptions, completed: fetchTimeZoneCompleted } = useDropdown('timeZones')
  const { setToastContent } = useToastContext()


  // Define the form fields and contents
  const formDefinition = getGroupFormDefinition({
    defaultValues: undefined,
    selectOptions: {
      timeZone: timeZonesOptions,
    },
  })

  const handleToggleStatus = async (id: string) => {
    try {
      await dispatch(toggleGroupStatus({ id }))
      setToastContent({
        open: true,
        severity: 'success',
        title: 'Success',
        content: 'Group status updated successfully',
      })
    } catch (error) {
      setToastContent({
        open: true,
        severity: 'error',
        title: 'Error',
        content: 'Failed to update depot status',
      })
    }
  }

  const handleSubmit = (data: Record<string, unknown>) => {
    setSubmitting(true)

    const selectedGroup = groups.find(group => group.id === rowSelectionModel)
    const mapCenter = data.mapCenter as MapFieldValue

    if (formType === 'edit' && selectedGroup) {
      // Handle add group
      const editPayload = {
        ...selectedGroup,
        name: data.name as string,
        orderHasLineItem: data.orderHasLineItem as boolean,
        isActive: data.isActive as boolean,
        timeZone: (data.timeZone as { value: string })?.value,
        x: mapCenter?.longitude,
        y: mapCenter?.latitude,
        zoomLevel: mapCenter?.zoomLevel,
      }

      dispatch(saveGroup({
        group: editPayload,
      })).then(() => {
        setFormType('view')
      })
    } else {
      const addPayload = {
        name: data.name as string,
        orderHasLineItem: data.orderHasLineItem as boolean,
        isActive: data.isActive as boolean,
        timeZone: (data.timeZone as { value: string })?.value,
        x: mapCenter?.longitude,
        y: mapCenter?.latitude,
        zoomLevel: mapCenter?.zoomLevel,
      }

      dispatch(saveGroup({
        group: addPayload,
      })).unwrap().then((group) => {
        setFormType('view')
        setRowSelectionModel(group.id)
      })
    }

    setSubmitting(false)
  }

  // Update form values when selectedId changes
  const resetForm = () => {
    if (formType === 'add') {
      setFormValues(initialGroupFormValues)
    }
    else {
      const selectedGroup = groups.find(group => group.id === rowSelectionModel)

      setFormValues({
        name: selectedGroup?.name || '',
        orderHasLineItem: selectedGroup?.orderHasLineItem || false,
        isActive: selectedGroup?.isActive || false,
        timeZone: timeZonesOptions.find(tz => tz.value === selectedGroup?.timeZone) || undefined,
        mapCenter: {
          latitude: selectedGroup?.y || DEFAULT_MAP_CENTER_LAT,
          longitude: selectedGroup?.x || DEFAULT_MAP_CENTER_LON,
          zoomLevel: selectedGroup?.zoomLevel || DEFAULT_MAP_ZOOM,
        },
      })
    }
  }

  useEffect(() => {
    if (!rowSelectionModel || !['edit', 'view'].includes(formType) || !fetchTimeZoneCompleted) return

    resetForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelectionModel, groups, formType, setFormValues, fetchTimeZoneCompleted])

  // Define the columns and rows for the grid
  const rows = groups.map(item => ({
    ...item,
    isSelectable: true,
  }))

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 150,
      minWidth: 40,
      filterable: true,
      sortable: true,
      pinnable: false,
      type: 'string',
    },
    {
      field: 'orderHasLineItem',
      headerName: 'Order Line Items',
      width: 98,
      minWidth: 40,
      filterable: true,
      sortable: false,
      pinnable: false,
      type: 'string',
      valueGetter: (value) => value ? 'Yes' : 'No',
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 98,
      minWidth: 40,
      filterable: true,
      sortable: false,
      pinnable: false,
      type: 'string',
      valueGetter: (value) => value ? 'Active' : 'Disabled',
    },
  ]

  const onViewRowDetail = (row: GridValidRowModel) => {
    setFormType('view')
    setShowDetails(true)
    setRowSelectionModel(row.id)
  }

  const onHandleAdd = () => {
    setFormType('add')
    setShowDetails(true)
    setRowSelectionModel(undefined)
    setFormValues(initialGroupFormValues)
  }

  const handleCloseDetail = () => {
    setRowSelectionModel(undefined)
    setShowDetails(false)
  }

  return (
    <StyledContainer>
      <Allotment separator={true} defaultSizes={[100, 100]}>
        <Allotment.Pane>
          <StyledLeftPane>
            <Grid container direction={'row'} alignItems={'flex-start'} justifyContent={'space-between'} wrap="nowrap">
              <Grid item>
                <StyledHeader variant="body1">Groups</StyledHeader>
                <StyledDescription>
                  Organise your operations based on your business structure. Create separate groups for different locations or business units while keeping everything connected.
                </StyledDescription>
              </Grid>
              <Grid item>
                <StyledAddButton
                  primary
                  startIcon={<Icon icon={faAdd} />}
                  onClick={onHandleAdd}
                  sx={{ minWidth: pxToRem(130) }}
                >
                  New Group
                </StyledAddButton>
              </Grid>
            </Grid>
            <Divider sx={{ marginTop: pxToRem(16) }} />
            <StyledGridTable
              rows={rows}
              columns={columns}
              isDragDropEnabled={false}
              rowSelectionModel={rowSelectionModel}
              onRowClick={onViewRowDetail}
              initialState={{
                filter: {
                  filterModel: {
                    items: [{
                      field: 'isActive', operator: 'equals', value: 'Active',
                    }],
                  },
                },
              }}
              slots={{
                toolbar: CustomGridToolbar,
              }}
              sx={{
                '.MuiDataGrid-filler--pinnedRight': { display: 'none' },
              }}
            />
          </StyledLeftPane>
        </Allotment.Pane>
        <Allotment.Pane visible={showDetails} minSize={360}>
          <StyledRightPane $visible={showDetails}>
            <Grid container direction={'row'} alignItems={'flex-start'} justifyContent={'space-between'} wrap="nowrap">
              <Grid item>
                <StyledHeader variant="body1" sx={{ paddingLeft: 0 }}>
                  {['edit', 'view'].includes(formType) ? `${formType} ${formValues?.name || ''} details` : 'Add a new group'}
                </StyledHeader>
              </Grid>
              <StyledButtonsContainer item>
                <ToggleButton
                  value="active"
                  primary
                  text
                  selected={false}
                  disabled={formType === 'add' || !rowSelectionModel}
                  onChange={() => handleToggleStatus(rowSelectionModel as string)}
                >
                  <Icon icon={faToggleOn} sx={{ fontSize: pxToRem(14), marginRight: pxToRem(4) }} />
                  Toggle Status
                </ToggleButton>
                <ToggleButton
                  value="checked"
                  primary
                  text
                  disabled={formType === 'add' || !rowSelectionModel}
                  selected={formType === 'edit'}
                  onChange={() => {
                    resetForm()
                    setFormType((prev) => prev === 'edit' ? 'view' : 'edit')
                  }}
                  sx={{ minWidth: pxToRem(100) }}
                >
                  <Icon icon={faEdit} sx={{ fontSize: pxToRem(14), marginRight: pxToRem(4) }} />
                  {formType === 'edit' ? 'Cancel Edit' : 'Edit Group'}
                </ToggleButton>
                <IconButton size="small" onClick={() => handleCloseDetail()}>
                  <Icon icon={faClose} sx={{ fontSize: pxToRem(18) }} />
                </IconButton>
              </StyledButtonsContainer>
            </Grid>
            <DynamicForm
              {...formDefinition}
              initialValues={formValues}
              onSubmit={handleSubmit}
              isReadOnly={isSubmitting || !showDetails || formType === 'view'}
              onCancel={() => {
                resetForm()

                if (formType === 'add') {
                  setShowDetails(false)
                }

                setFormType('view')
              }}
              slotProps={{
                submitButton: {
                  disabled: isSubmitting,
                },
              }}
            />
          </StyledRightPane>
        </Allotment.Pane>
      </Allotment>
    </StyledContainer>
  )
}

const StyledGridTable = styled(GridTable)`
  height: calc(100% - ${pxToRem(86)});
`